import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    inject,
    signal,
    WritableSignal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import {
    CategoryConfigs,
    SE_ATTENTION_POLL_MS,
    StudentEngagementUtil,
    ViewMode,
    ViewModeCategory,
} from "./student.engagement.util";
import { SeSecurityStore } from "./se.security.store";
import { S25CollapseChevronComponent } from "../s25-collapse-chevron/s25.collapse.chevron.component";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { s25Resource } from "../../util/s25.resource";
import { ContactService } from "../../services/contact.service";
import { S25ContactProfileImageComponent } from "./contact/contact.profile.image.component";
import { StateStore } from "../../services/state/state.store";
import { S25ItemI } from "../../pojo/S25ItemI";
import { S25Util } from "../../util/s25-util";
import { NameService } from "../../services/name.service";
import { Item } from "../../pojo/Item";
import { StandoutClassroomService } from "../../services/standout.classroom.service";
import { Subscription } from "rxjs";
import { OnDestroy } from "@angular/core";
import { WorkflowService } from "../workflow/service/workflow.service";
import { DataIngestionService } from "../data-ingestion/data.ingestion.service";
import { ElectionEligibleStore } from "./elections/election.eligible.store";
import { LoginStore } from "../../stores/login.store";
import { SeSidebarStore } from "./se.sidebar.store";
import { S25Const } from "../../util/s25-const";
import { SeConnect25ThemeStore } from "./se.connect25.theme.store";
import { TelemetryService } from "../../services/telemetry.service";

interface SidebarSection {
    category: ViewModeCategory;
    config: (typeof CategoryConfigs)[ViewModeCategory];
    modes: ViewMode[];
    expanded: WritableSignal<boolean>;
}

@TypeManagerDecorator("se-sidebar")
@Component({
    selector: "se-sidebar",
    template: `
        <div class="sidebar-backdrop" [class.visible]="sidebarStore.isOpen()" (click)="sidebarStore.close()"></div>
        <aside
            data-testid="seSidebar"
            class="sidebar"
            id="se-sidebar"
            [class.open]="sidebarStore.isOpen()"
            [class.in-app]="inApp"
        >
            <!-- User Section -->
            @if (contactId() && personalSection()) {
                <div class="user-section">
                    <a
                        s25-ng-router-link
                        [stateName]="'seUserSettings'"
                        class="collapse-btn-link"
                        (click)="$event.stopPropagation()"
                    >
                        <s25-ng-contact-profile-image
                            [contactId]="contactId()"
                            [name]="contactName()"
                            [interactive]="profileImageInteractive()"
                            [interactiveTooltip]="'User settings'"
                        ></s25-ng-contact-profile-image>
                    </a>
                    <div class="user-info">{{ contactName() }}</div>
                    <s25-ng-button
                        type="none"
                        class="user-section-toggle"
                        ariaLabel="Toggle personal menu"
                        (clicked)="personalSection()!.expanded.set(!personalSection()!.expanded())"
                    >
                        <s25-ng-collapse-chevron
                            [expanded]="personalSection()!.expanded()"
                            [collapsedOrientation]="'right'"
                            [size]="18"
                        ></s25-ng-collapse-chevron>
                    </s25-ng-button>
                </div>

                @if (personalSection()!.expanded() && personalSection()!.modes.length) {
                    <nav class="menu personal-menu">
                        @for (view of personalSection()!.modes; track view.stateName) {
                            @let isActive = currentActiveState() === view.stateName;
                            @let navState = getNavigationState(view);
                            <a
                                s25-ng-router-link
                                [stateName]="navState.stateName"
                                [stateParams]="navState.stateParams"
                                class="menu-item"
                                [class.active]="isActive"
                                [attr.data-label]="view.stateName"
                                [attr.aria-current]="isActive ? 'page' : null"
                                [attr.aria-label]="navLinkAriaLabel(view)"
                                (click)="onNavClick()"
                            >
                                <span class="icon-wrapper">
                                    <s25-ng-icon
                                        class="menu-icon"
                                        [type]="view.iconProp!"
                                        [size]="20"
                                        [autoDarkMode]="true"
                                        [ariaLabel]="false"
                                    />
                                    @if (showAttentionDot(view)) {
                                        <span class="unread-dot unread-dot--icon" aria-hidden="true"></span>
                                    }
                                </span>
                                <span class="menu-label">
                                    {{ view.name }}
                                    @if (showAttentionDot(view)) {
                                        <span class="unread-dot unread-dot--label" aria-hidden="true"></span>
                                    }
                                </span>
                            </a>
                        }
                    </nav>
                }

                @if (eligibleElections().length > 0) {
                    <div class="c-margin-top--single c-margin-bottom--single">
                        <div class="section-subheader">Voting</div>
                        <nav class="menu">
                            @for (e of eligibleElections(); track e.electionId) {
                                <a
                                    s25-ng-router-link
                                    [stateName]="'seElectionBallot'"
                                    [stateParams]="{ electionId: e.electionId }"
                                    class="menu-item"
                                    (click)="onNavClick()"
                                >
                                    <span class="menu-label">Vote: {{ e.name }}</span>
                                </a>
                            }
                        </nav>
                    </div>
                }

                @if (personalSection()!.expanded() || sidebarSections().length > 0) {
                    <div class="divider"></div>
                }
            }

            @for (section of sidebarSections(); track section.category) {
                @if (section.modes.length) {
                    <div
                        class="section-container"
                        [class.general-section]="section.category === ViewModeCategory.General"
                    >
                        <!-- Section header (except for General which shows items directly) -->
                        @if (section.category !== ViewModeCategory.General) {
                            <button
                                type="button"
                                class="section-header-with-icon"
                                (click)="section.expanded.set(!section.expanded())"
                                [attr.aria-expanded]="section.expanded()"
                                [attr.aria-controls]="sectionMenuId(section.category)"
                            >
                                <s25-ng-icon
                                    [type]="section.config.icon"
                                    [size]="20"
                                    [autoDarkMode]="true"
                                    [ariaLabel]="false"
                                />
                                <span>{{ section.config.label }}</span>
                                <s25-ng-icon
                                    class="section-header-chevron"
                                    [class.section-header-chevron--expanded]="section.expanded()"
                                    [type]="'caretRight'"
                                    [size]="16"
                                    [ariaLabel]="false"
                                />
                            </button>
                        }

                        <!-- Menu items -->
                        @if (section.category === ViewModeCategory.General || section.expanded()) {
                            <nav
                                class="menu"
                                [class.expanded-menu]="section.expanded()"
                                [id]="sectionMenuId(section.category)"
                            >
                                @for (view of section.modes; track view.stateName) {
                                    @let isActive = currentActiveState() === view.stateName;
                                    @let navState = getNavigationState(view);
                                    <a
                                        s25-ng-router-link
                                        [stateName]="navState.stateName"
                                        [stateParams]="navState.stateParams"
                                        class="menu-item"
                                        [class.active]="isActive"
                                        [attr.data-label]="view.stateName"
                                        [attr.aria-current]="isActive ? 'page' : null"
                                        [attr.aria-label]="navLinkAriaLabel(view)"
                                        (click)="onNavClick()"
                                    >
                                        <span class="icon-wrapper">
                                            <s25-ng-icon
                                                [type]="view.iconProp!"
                                                [size]="20"
                                                [autoDarkMode]="true"
                                                [ariaLabel]="false"
                                            />
                                            @if (showAttentionDot(view)) {
                                                <span class="unread-dot unread-dot--icon" aria-hidden="true"></span>
                                            }
                                        </span>
                                        <span class="menu-label">
                                            {{ view.name }}
                                            @if (showAttentionDot(view)) {
                                                <span class="unread-dot unread-dot--label" aria-hidden="true"></span>
                                            }
                                        </span>
                                    </a>
                                }
                            </nav>
                        }
                    </div>

                    @if (!$last && section.category !== ViewModeCategory.General) {
                        <div class="divider"></div>
                    }
                }
            }

            @if (supportMailto(); as mailto) {
                <div class="divider"></div>
                <nav class="menu" aria-label="Support">
                    <a class="menu-item support-link" [href]="mailto" (click)="onSupportClick()">
                        <span class="icon-wrapper">
                            <s25-ng-icon [type]="'help'" [size]="20" [autoDarkMode]="true" [ariaLabel]="false" />
                        </span>
                        <span class="menu-label">Contact Support</span>
                    </a>
                </nav>
            }
        </aside>
    `,
    styles: `
        :host {
            display: block;
        }

        .sidebar-backdrop {
            display: none;
        }

        .sidebar {
            width: 0;
            background: var(--color--surface-bg);
            border-radius: 16px;
            padding: 0;
            box-shadow: 0 1px 3px color-mix(in srgb, var(--color--text) 10%, transparent);
            overflow: hidden;
            transition:
                width var(--transition-medium) ease,
                padding var(--transition-medium) ease;
        }

        .sidebar.open {
            width: 280px;
            padding: 24px 16px;
        }

        @media (max-width: 930px) {
            :host {
                position: fixed;
                top: 0;
                left: 0;
                z-index: 1000;
                pointer-events: none;
            }

            .sidebar-backdrop {
                display: block;
                position: fixed;
                inset: 0;
                background: color-mix(in srgb, var(--color--text-strong) 40%, transparent);
                opacity: 0;
                visibility: hidden;
                transition:
                    opacity var(--transition-medium) ease,
                    visibility var(--transition-medium) ease;
                pointer-events: none;
            }

            .sidebar-backdrop.visible {
                opacity: 1;
                visibility: visible;
                pointer-events: auto;
            }

            .sidebar {
                position: fixed;
                top: 0;
                left: 0;
                height: 100vh;
                width: 280px;
                padding: 24px 16px;
                border-radius: 0 16px 16px 0;
                transform: translateX(-100%);
                transition: transform var(--transition-medium) ease;
                overflow-y: auto;
                z-index: 1001;
                pointer-events: auto;
            }

            .sidebar.open {
                transform: translateX(0);
            }

            .sidebar.in-app {
                padding-top: env(safe-area-inset-top);
            }
        }

        .user-section {
            display: flex;
            align-items: center;
            gap: 12px;
            border-radius: 12px;
            margin-bottom: 24px;
        }

        .user-section:hover {
            cursor: pointer;
        }

        .collapse-btn-link {
            display: inline-flex;
            line-height: 0;
            border-radius: 50%;
        }

        .user-info {
            flex: 1;
            min-width: 0;
            font-weight: 600;
            color: var(--color--text-strong);
            font-size: 14px;
        }

        .section-subheader {
            font-size: 12px;
            color: var(--color--text-muted);
            margin-bottom: 4px;
        }

        .divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, var(--color--border), transparent);
            margin: 20px 0 !important;
        }

        /* Match .menu-item / .menu-label; do not use font shorthand inherit (resets size to UA button). */
        button.section-header-with-icon {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.25;
            color: var(--color--text-muted);
            padding: 10px 14px;
            margin: 2px 0 0 0;
            border-radius: 8px;
            transition: all var(--transition-medium) ease;
            cursor: pointer;
            user-select: none;
            width: 100%;
            text-align: left;
            background: none;
            border: 0;
        }

        button.section-header-with-icon:hover {
            background: var(--color--surface-bg-hover);
            color: var(--color--text);
        }

        button.section-header-with-icon s25-ng-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--color--text-muted);
            transition: color var(--transition-medium) ease;
        }

        button.section-header-with-icon:hover s25-ng-icon {
            color: var(--color--text);
        }

        button.section-header-with-icon span {
            flex: 1;
        }

        .section-header-chevron {
            margin-left: auto;
            flex-shrink: 0;
        }

        .section-header-chevron ::ng-deep svg {
            color: var(--color--text-muted);
            transition: color var(--transition-medium) ease;
        }

        button.section-header-with-icon:hover .section-header-chevron ::ng-deep svg {
            color: var(--color--text);
        }

        .section-header-chevron--expanded {
            --icon--rotation-degrees: 90deg;
        }

        s25-ng-button.user-section-toggle {
            margin-left: auto;
            flex-shrink: 0;
        }

        ::ng-deep s25-ng-collapse-chevron svg {
            color: var(--color--text-muted);
            transition: color var(--transition-medium) ease;
        }

        .user-section:hover ::ng-deep s25-ng-collapse-chevron svg {
            color: var(--color--text);
        }

        .section-container {
            margin-bottom: 8px;
        }

        .section-container.general-section {
            margin-bottom: 16px;
        }

        .menu {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        .menu.expanded-menu {
            padding-left: 28px;
            margin-top: 4px;
        }

        .general-section .menu {
            margin-bottom: 4px;
        }

        .menu.personal-menu {
            padding-left: 28px;
            margin-top: 8px;
            margin-bottom: 8px;
        }

        .menu-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 14px;
            border-radius: 8px;
            --color--link-text: var(--color--text-muted);
            --color--link-text-hover: var(--color--text-strong);
            text-decoration: none;
            cursor: pointer;
            transition: all var(--transition-medium);
            position: relative;
            overflow: hidden;
        }

        .menu-item:hover {
            background: var(--color--surface-bg-hover);
            transform: translateX(2px);
        }

        .menu-item.active {
            background: color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            /* navy (not --cn-blue): 4.5:1+ on the tinted active background */
            --color--link-text: var(--cn-navy);
            --color--link-text-hover: var(--cn-navy);
            outline-offset: 0;
            font-weight: 600;
        }

        .menu-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .menu-label {
            font-size: 14px;
            white-space: nowrap;
            color: inherit;
        }

        .icon-wrapper {
            position: relative;
            display: flex;
            align-items: center;
            flex-shrink: 0;
        }

        .unread-dot {
            width: 8px;
            height: 8px;
            background-color: var(--color--danger);
            border-radius: 50%;
        }

        .unread-dot--icon {
            position: absolute;
            top: 0;
            right: -2px;
            display: none;
        }

        .unread-dot--label {
            display: inline-block;
            vertical-align: super;
            margin-left: 2px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25IconComponent,
        S25RouterLinkDirective,
        S25CollapseChevronComponent,
        S25ButtonComponent,
        S25ContactProfileImageComponent,
    ],
    standalone: true,
})
export class SeSidebarComponent implements OnDestroy {
    protected readonly ViewModeCategory = ViewModeCategory;
    private securityStore = inject(SeSecurityStore);
    private readonly electionEligible = inject(ElectionEligibleStore);
    protected readonly eligibleElections = this.electionEligible.elections;

    readonly loginStore = inject(LoginStore);
    protected stateStore = inject(StateStore);
    readonly sidebarStore = inject(SeSidebarStore);
    private readonly themeStore = inject(SeConnect25ThemeStore);

    protected readonly supportMailto = computed(() => {
        const supportEmail = this.themeStore.supportEmail();
        return supportEmail ? `mailto:${supportEmail}` : "";
    });

    protected readonly inApp = S25Const.inApp;

    private expandSignals = new Map<ViewModeCategory, WritableSignal<boolean>>();

    protected hasUnreadChats = signal(false);
    protected readonly hasWorkflowAttention = computed(() => this.securityStore.assignedWorkflowPendingCount() > 0);
    protected hasFormsAttention = signal(false);
    private unreadChatsSubscription?: Subscription;

    readonly loggedIn = computed(() => this.loginStore.loggedIn());

    private cd = inject(ChangeDetectorRef);

    constructor() {
        // Trigger initialization outside of computed to avoid signal write errors
        effect(() => {
            if (this.loggedIn() && !this.securityStore.isInitialized()) {
                this.securityStore.ensureInitialized().catch((error: unknown) => {
                    console.error("Failed to initialize security store:", error);
                });
            }
        });

        // Workflow assigned count for nav access (fullFlsOrWorkflowAssignee), workflow attention dot, and sidebar computeds.
        effect((onCleanup) => {
            if (!this.securityStore.isInitialized()) return;

            if (this.loggedIn()) {
                this.pollWorkflowAssignedCount();
                const timer = setInterval(() => this.pollWorkflowAssignedCount(), SE_ATTENTION_POLL_MS);
                onCleanup(() => clearInterval(timer));
            }
        });

        effect((onCleanup) => {
            if (this.loggedIn()) {
                this.pollFormsAttention();
                const timer = setInterval(() => this.pollFormsAttention(), SE_ATTENTION_POLL_MS);
                onCleanup(() => clearInterval(timer));
            } else {
                this.hasFormsAttention.set(false);
            }
        });

        effect((onCleanup) => {
            if (this.loggedIn() && this.securityStore.canAccessChat()) {
                void this.checkUnreadChats();
                const handleFocus = (): void => void StandoutClassroomService.forceRefreshUnreadChats();
                window.addEventListener("focus", handleFocus);
                onCleanup(() => {
                    this.unreadChatsSubscription?.unsubscribe();
                    this.unreadChatsSubscription = undefined;
                    window.removeEventListener("focus", handleFocus);
                });
            } else {
                this.hasUnreadChats.set(false);
                this.unreadChatsSubscription?.unsubscribe();
                this.unreadChatsSubscription = undefined;
            }
        });
    }

    ngOnDestroy() {
        this.unreadChatsSubscription?.unsubscribe();
    }

    private async checkUnreadChats() {
        if (!this.securityStore.canAccessChat()) {
            this.hasUnreadChats.set(false);
            return;
        }
        try {
            const unreadChats = await StandoutClassroomService.getUnreadChatsWithRecurringCheck();
            this.hasUnreadChats.set(unreadChats.length > 0);
        } catch (error) {
            console.error("Error checking unread chats:", error);
            this.hasUnreadChats.set(false);
        }
        this.unreadChatsSubscription?.unsubscribe();
        this.unreadChatsSubscription = StandoutClassroomService.getUnreadChatsObservable().subscribe((unreadChats) => {
            this.hasUnreadChats.set(unreadChats.length > 0);
            this.cd.detectChanges();
        });
    }

    private pollWorkflowAssignedCount() {
        WorkflowService.getAssignedWorkflowCount()
            .then((count) => {
                this.securityStore.setAssignedWorkflowPendingCount(count);
            })
            .catch(() => {});
    }

    private pollFormsAttention() {
        DataIngestionService.hasSubmissionsNeedingAttention()
            .then((value) => this.hasFormsAttention.set(!!value))
            .catch(() => this.hasFormsAttention.set(false));
    }

    protected showAttentionDot(view: ViewMode): boolean {
        switch (view.id) {
            case "chat":
                return this.hasUnreadChats();
            case "myWorkflow":
                return this.hasWorkflowAttention();
            case "myForms":
                return this.hasFormsAttention();
            default:
                return false;
        }
    }

    protected navLinkAriaLabel(view: ViewMode): string | null {
        if (!this.showAttentionDot(view)) {
            return null;
        }
        if (view.id === "chat") {
            return `${view.name}, unread`;
        }
        return `${view.name}, needs attention`;
    }
    /**
     * Computed signal that returns the current state name
     * Uses centralized StateStore for route tracking
     */
    protected currentActiveState = computed(() => this.stateStore.currentSeState());

    /**
     * Reactively computed available view modes based on user permissions
     * Automatically updates when security store initializes or permissions change
     */
    protected sidebarSections = computed<SidebarSection[]>(() => {
        if (this.loggedIn() && !this.securityStore.isInitialized()) {
            return [];
        }

        this.securityStore.assignedWorkflowPendingCount();

        const objectData = this.objectData(),
            objectName = this.objectName.value();
        const categorizedModes = StudentEngagementUtil.getCategorizedViewModes();
        const sections: SidebarSection[] = [];

        Object.values(CategoryConfigs)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .forEach((config) => {
                if (this.getConfigCategoriesToSkip(objectData).includes(config.id)) return;

                const modes = categorizedModes.get(config.id) || [];
                let filteredModes = this.filterViewModesSync(modes);

                if (filteredModes.length > 0) {
                    if (
                        objectName &&
                        objectData &&
                        [ViewModeCategory.Event, ViewModeCategory.Organization].includes(config.id)
                    ) {
                        config.label = objectName;
                        filteredModes = filteredModes.map((mode) => ({
                            ...mode,
                            stateParams: { itemId: objectData.itemId },
                        }));
                    }

                    sections.push({
                        category: config.id,
                        config,
                        modes: filteredModes.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)),
                        expanded: this.getOrCreateExpandSignal(config.id, config.defaultExpanded ?? false),
                    });
                }
            });

        return sections;
    });

    protected personalSection = computed<SidebarSection | undefined>(() => {
        if (!this.securityStore.isInitialized()) {
            return undefined;
        }

        this.securityStore.assignedWorkflowPendingCount();

        const personalModes = StudentEngagementUtil.getViewModesByCategory(ViewModeCategory.Personal);
        const filteredModes = this.filterViewModesSync(personalModes);

        if (filteredModes.length > 0) {
            const config = CategoryConfigs[ViewModeCategory.Personal];
            return {
                category: ViewModeCategory.Personal,
                config,
                modes: filteredModes.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)),
                expanded: this.getOrCreateExpandSignal(ViewModeCategory.Personal, config.defaultExpanded ?? false),
            };
        }

        return undefined;
    });

    private objectData = computed<S25ItemI.NumId | undefined>(() => {
        const itemTypeId = this.stateStore.seObjTypeId(),
            itemId = this.stateStore.seItemId();
        if (itemId && itemTypeId) return { itemId, itemTypeId };
    });

    private objectName = s25Resource({
        if: () => !!this.objectData(),
        params: () => this.objectData(),
        loader: async () => {
            const [names] = await S25Util.Maybe(NameService.getNames([this.objectData()!]));
            if (names) return Object.values(names)[0];
        },
        defaultValue: "",
    });

    private getOrCreateExpandSignal(category: ViewModeCategory, defaultValue: boolean): WritableSignal<boolean> {
        if (!this.expandSignals.has(category)) {
            this.expandSignals.set(category, signal(defaultValue));
        }
        return this.expandSignals.get(category)!;
    }

    private contact = s25Resource({
        if: () => this.loggedIn(),
        loader: async () => ContactService.getCurrentContact("", []),
    });

    protected contactId = computed(() => this.contact.value()?.contacts?.contact?.contact_id);
    protected contactName = computed(() => this.contact.value()?.contacts?.contact?.contact_name ?? "");

    /** Same gate as User Settings / profile edit: SE read FLS + current contact. */
    protected profileImageInteractive = computed(() => !!this.contactId() && this.securityStore.hasReadFls());

    /**
     * Synchronous version of view mode filtering
     * Uses signal values from security store for reactive filtering
     */
    private filterViewModesSync(modes: readonly ViewMode[]): ViewMode[] {
        const results = modes
            .map((mode) => {
                const hasAccess = this.checkViewModeAccessSync(mode);
                if (!hasAccess) return null;
                return mode;
            })
            .filter((mode): mode is ViewMode => mode !== null);

        return results;
    }

    /**
     * Synchronous version of view mode access check
     * Uses signal values from security store
     */
    private checkViewModeAccessSync(mode: ViewMode): boolean {
        // Always show if marked as default
        if (mode.showByDefault) return true;

        // Use custom function if provided
        if (mode.hasAccessFunction) {
            // Can't handle async functions in computed, skip these
            return false;
        }

        if (this.loggedIn() && mode.requiredRight) {
            return StudentEngagementUtil.requiredRightAllowsAccess(mode.requiredRight, this.securityStore);
        }

        return false;
    }

    /**
     * Get the navigation state name and params for a view mode
     * All ViewModes should have stateName set, so this is just a type-safe getter
     */
    protected getNavigationState(view: ViewMode): {
        stateName: string;
        stateParams?: Record<string, string | number>;
    } {
        // All ViewModes should have stateName now that we're using UI-Router states
        return { stateName: view.stateName!, stateParams: view.stateParams };
    }

    /** Stable id for section nav + `aria-controls` on the section expand button. */
    protected sectionMenuId(category: ViewModeCategory): string {
        return `se-sidebar-section-${category}`;
    }

    getConfigCategoriesToSkip(objectData: S25ItemI.NumId | undefined) {
        return objectData
            ? [
                  ViewModeCategory.Personal,
                  objectData.itemTypeId === Item.Ids.Event ? ViewModeCategory.Organization : ViewModeCategory.Event,
                  // Org Service Hours lives on org home tabs; revert: remove this skip to restore sidebar link.
                  ViewModeCategory.Organization,
                  ViewModeCategory.Users,
              ]
            : [ViewModeCategory.Personal, ViewModeCategory.Organization, ViewModeCategory.Event];
    }

    protected onSupportClick() {
        TelemetryService.sendWithSub("Connect25", "Sidebar", "ContactSupport");
    }

    protected onNavClick() {
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (window.innerWidth <= 930) {
            this.sidebarStore.close();
        }
    }
}
